'use client';

import Image from 'next/image';
import { useRef, useState } from 'react';
import {
    AnimatePresence,
    motion,
    useMotionValueEvent,
    useReducedMotion,
    useScroll,
    useTransform,
} from 'framer-motion';

import CountUp from '../animations/CountUp';

interface Stat {
    label: string;
    value: string;
    suffix?: string | null;
}

interface HomeStatsBandProps {
    stats: Stat[];
    locale: 'en' | 'ar';
}

const metricImages = [
    '/assets/profile/impact-five-years.webp',
    '/assets/profile/impact-fifty-clients.webp',
    '/assets/profile/impact-two-branches-v2.webp',
    '/assets/profile/impact-creative-team.webp',
];

const fallbackStats: Record<'en' | 'ar', Stat[]> = {
    en: [
        { label: 'Five years', value: '5', suffix: '+' },
        { label: 'More than fifty clients', value: '50', suffix: '+' },
        { label: 'Two markets, one team', value: '2' },
        { label: 'More than twenty-five creatives', value: '25', suffix: '+' },
    ],
    ar: [
        { label: 'خمس سنوات', value: '5', suffix: '+' },
        { label: 'أكثر من خمسين عميلاً', value: '50', suffix: '+' },
        { label: 'سوقان، فريق واحد', value: '2' },
        { label: 'أكثر من خمسة وعشرين مبدعًا', value: '25', suffix: '+' },
    ],
};

const storyCopy = {
    en: {
        eyebrow: 'nibras in numbers',
        title: 'Numbers that refuse to sit still.',
        instruction: 'Scroll through the story',
        descriptions: [
            'We grow with every idea we bring to life.',
            'Partners in impact, never just numbers.',
            'Saudi Arabia and Jordan, moving as one team.',
            'Different disciplines moving in one direction.',
        ],
    },
    ar: {
        eyebrow: 'أثر نبراس بالأرقام',
        title: 'أرقامنا لا تقف ساكنة.',
        instruction: 'مرّر لتكمل الحكاية',
        descriptions: [
            'نكبر مع كل فكرة نصنعها.',
            'شركاء أثر، وليسوا مجرد أرقام.',
            'الأردن والسعودية، ضمن فريق واحد.',
            'تخصصات مختلفة تتحرك في اتجاه واحد.',
        ],
    },
};

function MetricNumber({ stat, className = '' }: { stat: Stat; className?: string }) {
    const numericValue = Number.parseFloat(stat.value.replace(/[^0-9.]/g, ''));
    const hasNumber = Number.isFinite(numericValue);
    const hasPlus = stat.suffix === '+' || stat.value.includes('+');

    return (
        <div className={`flex items-start font-heading font-black leading-none ${className}`} dir="ltr">
            {hasPlus && <span className="mt-[0.08em] text-[0.42em] text-[#f1a54e]">+</span>}
            {hasNumber ? <CountUp to={numericValue} duration={1.8} /> : <span>{stat.value}</span>}
            {!hasPlus && stat.suffix && <span className="text-[0.42em] text-[#f1a54e]">{stat.suffix}</span>}
        </div>
    );
}

function MobileMetric({
    stat,
    image,
    description,
    index,
}: {
    stat: Stat;
    image: string;
    description: string;
    index: number;
}) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 56 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.22 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative border-t border-white/12 py-10"
        >
            <div className="relative aspect-[4/3] overflow-hidden rounded-[2rem] border border-white/12 bg-[#10374d]">
                <motion.div
                    initial={{ scale: 1.14 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                >
                    <Image src={image} alt={stat.label} fill sizes="100vw" className="object-cover" />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#071f2d]/90 via-transparent to-transparent" />
                <motion.span
                    aria-hidden="true"
                    initial={{ x: '-120%' }}
                    whileInView={{ x: '220%' }}
                    viewport={{ once: true, amount: 0.45 }}
                    transition={{ duration: 1.1, delay: 0.15 }}
                    className="absolute inset-y-0 w-24 skew-x-[-18deg] bg-gradient-to-r from-transparent via-[#f1a54e]/30 to-transparent blur-xl"
                />
                <span className="absolute start-5 top-5 text-xs font-black tracking-[0.22em] text-white/60">
                    {String(index + 1).padStart(2, '0')} / 04
                </span>
            </div>
            <div className="relative -mt-10 px-5">
                <MetricNumber stat={stat} className="text-[5.5rem] text-white" />
                <h3 className="mt-3 font-heading text-3xl font-black text-white">{stat.label}</h3>
                <p className="mt-3 text-lg leading-relaxed text-white/64">{description}</p>
            </div>
        </motion.article>
    );
}

export default function HomeStatsBand({ stats, locale }: HomeStatsBandProps) {
    const isAr = locale === 'ar';
    const copy = storyCopy[locale];
    const providedStats = stats?.filter((stat) => stat.label && stat.value) || [];
    const safeStats = providedStats.length === 4 ? providedStats : fallbackStats[locale];
    const sectionRef = useRef<HTMLDivElement>(null);
    const reduceMotion = useReducedMotion();
    const [activeIndex, setActiveIndex] = useState(0);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end'],
    });
    const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);
    const imageDrift = useTransform(scrollYProgress, [0, 1], reduceMotion ? ['0%', '0%'] : ['-3%', '3%']);

    useMotionValueEvent(scrollYProgress, 'change', (value) => {
        const nextIndex = Math.min(safeStats.length - 1, Math.floor(value * safeStats.length));
        setActiveIndex((current) => (current === nextIndex ? current : nextIndex));
    });

    const activeStat = safeStats[activeIndex];

    return (
        <section className="relative bg-transparent text-white" dir={isAr ? 'rtl' : 'ltr'}>
            <div
                ref={sectionRef}
                className="relative hidden lg:block"
                style={{ minHeight: `${safeStats.length * 100}vh` }}
            >
            <div className="sticky top-0 h-screen overflow-hidden">
                <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col px-8 pb-8 pt-20 xl:px-10">
                    <header className="flex items-end justify-between gap-8 pb-5">
                        <div>
                            <p className="inline-flex bg-brand-text px-3 py-1 text-xs font-black uppercase tracking-[0.28em] text-[color:var(--brand-ink)]">{copy.eyebrow}</p>
                            <h2 className="mt-2 font-heading text-3xl font-black xl:text-4xl">{copy.title}</h2>
                        </div>
                        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white/45">
                            {copy.instruction}
                            <motion.span
                                animate={reduceMotion ? undefined : { y: [0, 7, 0] }}
                                transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }}
                                className="text-xl text-[#f1a54e]"
                            >
                                ↓
                            </motion.span>
                        </p>
                    </header>

                    <div className="relative min-h-0 flex-1">
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.figure
                                key={`image-${activeIndex}`}
                                initial={reduceMotion ? { opacity: 1 } : { opacity: 0, scale: 0.9, rotateX: 8, filter: 'blur(14px)' }}
                                animate={{ opacity: 1, scale: 1, rotateX: 0, filter: 'blur(0px)' }}
                                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 1.04, rotateX: -5, filter: 'blur(10px)' }}
                                transition={{ duration: reduceMotion ? 0 : 0.65, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute inset-x-0 top-0 h-[57vh] overflow-hidden rounded-[2.5rem] border border-white/14 bg-[#10374d] shadow-[0_40px_120px_rgba(0,0,0,.45)]"
                                style={{ perspective: '1200px' }}
                            >
                                <motion.div style={{ y: imageDrift }} className="absolute -inset-y-[6%] inset-x-0">
                                    {/* Deliberately no `priority`. This band sits well below the
                                        fold, but `priority` still emits a <link rel=preload> in the
                                        document head — so it was fetching a third full-size image at
                                        high priority in direct competition with the hero, which is
                                        the actual LCP element. Trello P-01. */}
                                    <Image
                                        src={metricImages[activeIndex]}
                                        alt={activeStat.label}
                                        fill
                                        sizes="(max-width: 1280px) 92vw, 1240px"
                                        className="object-cover"
                                    />
                                </motion.div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/10" />
                                <div className="absolute inset-5 rounded-[1.8rem] border border-[#f1a54e]/20" />
                                <motion.span
                                    aria-hidden="true"
                                    initial={{ x: isAr ? '180%' : '-180%' }}
                                    animate={{ x: isAr ? '-260%' : '260%' }}
                                    transition={{ duration: reduceMotion ? 0 : 1.2, delay: 0.18 }}
                                    className="absolute inset-y-0 w-40 skew-x-[-18deg] bg-gradient-to-r from-transparent via-[#f1a54e]/24 to-transparent blur-2xl"
                                />
                            </motion.figure>
                        </AnimatePresence>

                        <AnimatePresence mode="wait" initial={false}>
                            <motion.article
                                key={`copy-${activeIndex}`}
                                initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 42 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -28 }}
                                transition={{ duration: reduceMotion ? 0 : 0.55, delay: reduceMotion ? 0 : 0.12 }}
                                className="absolute inset-x-8 bottom-0 grid items-end gap-8 xl:grid-cols-[0.62fr_1.38fr]"
                            >
                                <MetricNumber stat={activeStat} className="text-[7rem] xl:text-[9rem]" />
                                <div className="border-s-2 border-[#f1a54e] pb-3 ps-7">
                                    <p className="mb-3 text-xs font-black uppercase tracking-[0.22em] text-[#f1a54e]">
                                        {String(activeIndex + 1).padStart(2, '0')} / {String(safeStats.length).padStart(2, '0')}
                                    </p>
                                    <h3 className="font-heading text-4xl font-black xl:text-5xl">{activeStat.label}</h3>
                                    <p className="mt-3 text-lg leading-relaxed text-white/64 xl:text-xl">
                                        {copy.descriptions[activeIndex]}
                                    </p>
                                </div>
                            </motion.article>
                        </AnimatePresence>
                    </div>
                </div>

                <div className="absolute end-7 top-1/2 z-20 h-44 w-px -translate-y-1/2 bg-white/16">
                    <motion.span
                        style={{ scaleY: progressScale, transformOrigin: 'top' }}
                        className="absolute inset-0 bg-[#f1a54e]"
                    />
                    {safeStats.map((stat, index) => (
                        <span
                            key={stat.label}
                            className={`absolute end-[-5px] h-2.5 w-2.5 rounded-full border transition-all duration-500 ${
                                index === activeIndex
                                    ? 'scale-150 border-[#f1a54e] bg-[#f1a54e]'
                                    : 'border-white/35 bg-transparent'
                            }`}
                            style={{ top: `${(index / (safeStats.length - 1)) * 100}%` }}
                        />
                    ))}
                </div>
            </div>
            </div>

            <div className="relative px-6 py-20 lg:hidden">
                <div className="mx-auto max-w-xl">
                    <p className="inline-flex bg-brand-text px-3 py-1 text-xs font-black uppercase tracking-[0.28em] text-[color:var(--brand-ink)]">{copy.eyebrow}</p>
                    {/* Not an <h2>: the lg:block sibling above already carries this
                        exact text as the section's H2. Duplicating a heading tag
                        for a CSS-only responsive variant produces two identical
                        headings in the DOM regardless of which is visually shown. */}
                    <p className="mt-3 font-heading text-4xl font-black leading-tight" role="heading" aria-level={2}>{copy.title}</p>
                    <div className="mt-10">
                        {safeStats.map((stat, index) => (
                            <MobileMetric
                                key={stat.label}
                                stat={stat}
                                image={metricImages[index]}
                                description={copy.descriptions[index]}
                                index={index}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
